import { create } from 'zustand';
import { settingsApi, settingsValuesApi } from '../lib/apiClient';
import { toast } from '../lib/toast';
import { ResolvedSetting } from '../types/settings';

interface SettingsState {
  resolvedSettings: ResolvedSetting[];
  resolvedLoading: boolean;
  error: string | null;

  loadResolvedSettings: () => Promise<void>;
  setResolvedValue: (key: string, value: string | boolean) => void;
  setValue: (definitionId: number, value: string, scopeType?: string, scopeId?: string) => Promise<boolean>;
  isChatAiAgentEnabled: () => boolean;
  isAiBuilderDebugEnabled: () => boolean;
  isChatDebugUsageEnabled: () => boolean;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  resolvedSettings: [],
  resolvedLoading: false,
  error: null,

  loadResolvedSettings: async () => {
    set({ resolvedLoading: true, error: null });
    try {
      const data = await settingsApi.getResolved();
      const resolved = Array.isArray(data) ? data : data.settings || [];
      set({ resolvedSettings: resolved });
    } catch (e: any) {
      const msg = e?.message || 'Failed to load settings';
      set({ error: msg });
      toast.error(msg);
    } finally {
      set({ resolvedLoading: false });
    }
  },

  setResolvedValue: (key, value) => {
    set(state => ({
      resolvedSettings: state.resolvedSettings.map(s =>
        s.key === key ? { ...s, value } : s
      ),
    }));
  },

  isChatAiAgentEnabled: () => {
    const settings = get().resolvedSettings;
    const setting = settings.find(s => s.key === 'chat_ai_agent_enabled');
    if (!setting) return false;
    return setting.value === 'true' || setting.value === true;
  },

  isChatDebugUsageEnabled: () => {
    const settings = get().resolvedSettings;
    const setting = settings.find(s => s.key === 'chat_debug_usage_enabled');
    if (!setting) return false;
    return setting.value === 'true' || setting.value === true;
  },

  isAiBuilderDebugEnabled: () => {
    const settings = get().resolvedSettings;
    const setting = settings.find(s => s.key === 'ai_builder_debug_mode');
    if (!setting) return false;
    return setting.value === 'true' || setting.value === true;
  },

  setValue: async (definitionId, value, scopeType?, scopeId?) => {
    try {
      const body: any = { value };
      if (scopeType) body.scopeType = scopeType;
      if (scopeId) body.scopeId = scopeId;
      await settingsValuesApi.setValue(definitionId, body);
      await get().loadResolvedSettings();
      toast.success('Setting updated');
      return true;
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update setting');
      return false;
    }
  },
}));
